<template>
  <div class="client-grouped-view">
    <!-- No Clients message -->
    <div v-if="!loading && groupedFacilities.length === 0" class="text-center d-flex flex-column">
      <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
      <p class="font-weight-medium">No Facilities Available!</p>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="text-center d-flex flex-column">
      <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
      <p class="font-weight-medium">Loading Facilities...</p>
    </div>

    <!-- Client Groups -->
    <div v-else class="client-groups">
      <v-expansion-panels
        v-model="expandedPanels"
        multiple
        class="elevation-0">
        <!-- Facilities with Clients -->
        <v-expansion-panel
          v-for="group in groupedFacilities"
          :key="group.clientId || 'no-client'"
          class="mb-4">
          <v-expansion-panel-header class="client-header">
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <v-icon class="mr-3" color="primary">mdi-domain</v-icon>
                <div>
                  <h3 class="client-name">{{ group.clientName || 'No Client Assigned' }}</h3>
                  <span class="client-count text--secondary">
                    {{ group.facilities.length }} {{ group.facilities.length === 1 ? 'facility' : 'facilities' }}
                  </span>
                </div>
              </div>
              <v-chip
                :color="group.clientId ? 'primary' : 'grey'"
                :outlined="!group.clientId"
                small>
                {{ group.facilities.length }}
              </v-chip>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <div class="facilities-list pt-3">
              <!-- Card View - Using exact same structure as original grid view -->
              <v-row v-if="listView === 'cards'">
                <v-col v-for="facility in group.facilities" :key="facility.id" cols="12" sm="6" md="4" lg="2">
                  <v-hover v-slot="{ hover }">
                    <v-card
                      :class="{
                        bg2: !facility.imageUrl,
                      }"
                      :elevation="2"
                      @click="openFacility(facility.id)"
                      class="mx-auto my-8 rounded-lg gridCard">
                      <v-img
                        height="300"
                        :src="getImage(facility.imageUrl)"
                        class="white--text align-end"
                        gradient="to top, rgba(35, 35, 30, 1), rgba(0, 0, 0, 0)">
                        <div class="d-flex align-center flex-column card-img-footer">
                          <v-card-title
                            class="bg_force_white--text align-self-start"
                            :class="{ 'hovered-title': hover }"
                            >{{ facility.name }}</v-card-title
                          >
                          <v-card-subtitle 
                            class="bg_force_white--text align-self-start pt-0" 
                            v-if="facility.client">
                            Client: {{ facility.client.name }}
                          </v-card-subtitle>
                          <v-expand-transition>
                            <div
                              class="d-inline-flex pr-4 align-self-end card-actions"
                              :class="{ 'card-actions-hovered': hover }"
                              v-if="hover"
                              style="gap: 8px; transition: 0.5s">
                              <v-btn
                                v-has="permissions.Facilities.Update"
                                @click.stop="updateFacility(facility.id)"
                                fab
                                small
                                class="mb-2"
                                :color="$vuetify.theme.isDark ? 'bg1' : 'white'">
                                <v-icon color="success" class="ma-0 pa-0"> mdi-pencil-outline</v-icon>
                              </v-btn>
                              <v-btn
                                :loading="favLoading == facility.id"
                                fab
                                small
                                class="mb-2"
                                :color="$vuetify.theme.isDark ? 'bg1' : 'white'"
                                @click.stop="toggleFavorite(facility)">
                                <v-icon v-if="facility.isFavorite" color="infoColor"
                                  >mdi-star-circle</v-icon
                                >
                                <v-icon v-else color="infoColor">mdi-star-outline</v-icon>
                              </v-btn>
                              <v-btn
                                v-has="permissions.Facilities.Delete"
                                fab
                                small
                                class="mb-2"
                                @click.stop="deleteFacility(facility.id)"
                                :color="$vuetify.theme.isDark ? 'bg1' : 'white'">
                                <v-icon color="error" class="ma-0 pa-0"> mdi-delete-outline</v-icon>
                              </v-btn>
                            </div>
                          </v-expand-transition>
                        </div>
                      </v-img>
                    </v-card>
                  </v-hover>
                </v-col>
              </v-row>

              <!-- List View - Using exact same structure as original list view -->
              <div v-else style="gap: 1rem" class="d-flex flex-column">
                <div v-for="facility in group.facilities" :key="'p' + facility.id" style="width: 100%">
                  <v-card
                    class="custom-card px-5"
                    elevation="1"
                    @click="openFacility(facility.id)"
                    :class="{ 'py-5': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm }">
                    <v-row
                      align="center"
                      no-gutters
                      :class="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 'flex-column' : ''">
                      <v-img
                        :src="getImage(facility.imageUrl)"
                        :max-width="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? 200 : 100" />

                      <v-col :cols="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? '12' : '2'">
                        <v-card-title
                          class="card-title"
                          :class="{
                            'justify-center': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm,
                          }"
                          >{{ facility.name }}</v-card-title
                        >
                        <v-card-subtitle class="card-subtitle d-flex pt-2">
                          <img
                            class="me-2"
                            src="@public/img/user.svg"
                            contain
                            width="15"
                            height="20" />
                          <span class="subHeadLine">
                            Created by:
                            <span class="ms-2" v-if="facility.createdBy"
                              >{{ facility.createdBy.firstName + ' ' + facility.createdBy.lastName }}
                            </span>
                          </span>
                        </v-card-subtitle>
                        <v-card-subtitle class="card-subtitle d-flex pt-0" v-if="facility.client">
                          <img
                            class="me-2"
                            src="@public/img/user.svg"
                            contain
                            width="15"
                            height="20" />
                          <span class="subHeadLine">
                            Client:
                            <span class="ms-2">{{ facility.client.name }}</span>
                          </span>
                        </v-card-subtitle>
                      </v-col>

                      <v-spacer></v-spacer>

                      <v-col cols="auto">
                        <div class="d-flex flex-column">
                          <div class="actionButtons d-flex justify-space-between px-2 py-3">
                            <v-icon 
                              v-has="permissions.Facilities.Update" 
                              color="green"
                              @click.stop="updateFacility(facility.id)">mdi-pencil</v-icon>
                            <v-icon
                              @click.stop="toggleFavorite(facility)"
                              v-if="facility.isFavorite"
                              color="infoColor"
                              >mdi-star-circle</v-icon
                            >
                            <v-icon @click.stop="toggleFavorite(facility)" v-else color="infoColor"
                              >mdi-star-outline</v-icon
                            >
                            <v-icon 
                              v-has="permissions.Facilities.Delete" 
                              color="red"
                              @click.stop="deleteFacility(facility.id)">mdi-delete</v-icon>
                          </div>
                          <label class="subHeadLine">
                            <v-icon class="subHeadLine">mdi-clock-outline</v-icon> Created on:
                            {{ $formatDate(facility.createdAt) || 'Unknown' }}</label
                          >
                        </div>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
import { $formatDate, $has } from '@Utils';
import { mapGetters } from 'vuex';
import permissions from '@Share/config/permissions';

export default {
  name: 'ClientGroupedView',
  props: {
    facilities: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    listView: {
      type: String,
      default: 'cards', // 'cards' or 'list'
    },
  },
  data() {
    return {
      permissions,
      expandedPanels: [], // Track which panels are expanded
    };
  },
  computed: {
    ...mapGetters('FACILITY', {
      favLoading: 'GET_FAV_LOADING',
    }),
    groupedFacilities() {
      if (!this.facilities || this.facilities.length === 0) {
        return [];
      }

      // Group facilities by client
      const groups = {};
      
      this.facilities.forEach(facility => {
        const clientId = facility.client?.id || null;
        const clientName = facility.client?.name || null;
        
        if (!groups[clientId]) {
          groups[clientId] = {
            clientId,
            clientName,
            facilities: [],
          };
        }
        
        groups[clientId].facilities.push(facility);
      });

      // Convert to array and sort
      const groupsArray = Object.values(groups);
      
      // Sort: clients first (alphabetically), then "No Client" last
      return groupsArray.sort((a, b) => {
        if (!a.clientName && !b.clientName) return 0;
        if (!a.clientName) return 1;
        if (!b.clientName) return -1;
        return a.clientName.localeCompare(b.clientName);
      });
    },
  },
  watch: {
    groupedFacilities: {
      immediate: true,
      handler(newGroups) {
        // Auto-expand all panels by default
        this.expandedPanels = newGroups.map((_, index) => index);
      },
    },
  },
  methods: {
    $formatDate,
    $has,
    openFacility(id) {
      this.$emit('facility-selected', id);
    },
    toggleFavorite(facility) {
      this.$emit('toggle-favorite', facility);
    },
    updateFacility(id) {
      this.$emit('update-facility', id);
    },
    deleteFacility(id) {
      this.$emit('delete-facility', id);
    },
    getImage(img) {
      return img || require('../../../images/no-image-available.jpg');
    },
  },
};
</script>

<style lang="scss" scoped>
.client-grouped-view {
  .client-groups {
    .client-header {
      background: var(--v-bg2-base);
      border-radius: 8px;
      
      .client-name {
        font-size: 18px;
        font-weight: 500;
        margin: 0;
        color: var(--v-bg6-base);
      }
      
      .client-count {
        font-size: 14px;
        color: var(--v-bg6-lighten3);
      }
    }
  }
  
  // Override expansion panel styles
  .v-expansion-panel {
    &::before {
      box-shadow: none;
    }
    
    .v-expansion-panel-header {
      padding: 16px 20px;
      
      &--active {
        min-height: auto;
      }
    }
    
    .v-expansion-panel-content__wrap {
      padding: 0 20px 16px;
    }
  }
}
</style> 